/* eslint-disable react/react-in-jsx-scope */
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/libs/utils";
import { enUS, fr, Locale } from "date-fns/locale";
import { ChevronDown, ChevronDownIcon, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// 🔧 Helper pour parser une date "yyyy-MM-dd" venant de l'URL
function parseDate(dateStr: string | null): Date | undefined {
  if (!dateStr) return undefined;
  const [year, month, day] = dateStr.split("-");
  if (!year || !month || !day) return undefined;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

const locales: Record<string, Locale> = {
  fr: fr,
  en: enUS,
};

const DateFilter = ({
  handleChange,
  initialAfter,
  initialBefore,
}: {
  handleChange: (slug: string, value: string[]) => void;
  initialAfter?: string | null;
  initialBefore?: string | null;
}) => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "documents",
  });

  const [selectedValue, setSelectedValue] = useState<string | null>(null);

  const handleDateChange = (
    val: Date | undefined,
    label: "after" | "before"
  ) => {
    if (val) {
      const isoDate = val.toISOString().split("T")[0]; // ✅ format ISO
      handleChange(label, [isoDate]);
    } else {
      handleChange(label, []);
    }
  };

  const handleCheckboxChange = (optionValue: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedValue(optionValue);

      if (optionValue === "custom") {
        // 🔹 Custom : reset le champ `date` et garder after/before
        handleChange("date", ["null"]);
      } else {
        // 🔹 Shortcut (today, lastYear, etc.) : reset after/before
        handleChange("after", []);
        handleChange("before", []);
        handleChange("date", [optionValue]);
      }
    } else {
      // Quand on décoche → reset tout
      setSelectedValue(null);
      handleChange("date", ["null"]);
      handleChange("after", []);
      handleChange("before", []);
    }
  };

  return (
    <div className="relative">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="text-xs py-[3px] h-[26px] border rounded-md px-3 font-normal flex gap-1 items-center">
            {t("Date d'ajout")}
            <ChevronDown size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="max-h-64 overflow-auto transition-all"
        >
          <DropdownMenuCheckboxItem
            checked={selectedValue === "today"}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) =>
              handleCheckboxChange("today", checked)
            }
            className="flex items-center gap-2 w-full"
          >
            {t("Aujourd'hui")}
          </DropdownMenuCheckboxItem>

          <DropdownMenuCheckboxItem
            checked={selectedValue === "lastWeek"}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) =>
              handleCheckboxChange("lastWeek", checked)
            }
            className="flex items-center gap-2 w-full"
          >
            {t("7 derniers jours")}
          </DropdownMenuCheckboxItem>

          <DropdownMenuCheckboxItem
            checked={selectedValue === "lastMonth"}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) =>
              handleCheckboxChange("lastMonth", checked)
            }
            className="flex items-center gap-2 w-full"
          >
            {t("30 derniers jours")}
          </DropdownMenuCheckboxItem>

          <DropdownMenuCheckboxItem
            checked={selectedValue === "thisYear"}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) =>
              handleCheckboxChange("thisYear", checked)
            }
            className="flex items-center gap-2 w-full"
          >
            {t("Cette année")} ({new Date().getFullYear()})
          </DropdownMenuCheckboxItem>

          <DropdownMenuCheckboxItem
            checked={selectedValue === "lastYear"}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) =>
              handleCheckboxChange("lastYear", checked)
            }
            className="flex items-center gap-2 w-full"
          >
            {t("Année dernière")} ({new Date().getFullYear() - 1})
          </DropdownMenuCheckboxItem>

          <DropdownMenuCheckboxItem
            checked={selectedValue === "custom"}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) =>
              handleCheckboxChange("custom", checked)
            }
            className="flex items-center gap-2 w-full"
          >
            {t("Période personnalisée")}
            <ChevronRight />
          </DropdownMenuCheckboxItem>

          {selectedValue === "custom" && (
            <div
              className="w-full bg-white rounded-md rounded-l-none border bg-popover h-full absolute shadow-md top-0 p-4"
              style={{
                left: "calc(100% - 4px)",
                "--tw-shadow":
                  "4px 4px 7px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
              }}
            >
              <InputDate label="after" handleDateChange={handleDateChange} />
              <InputDate
                label="before"
                handleDateChange={handleDateChange}
                className="mt-4"
              />
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const InputDate = ({
  label,
  className,
  handleDateChange,
}: {
  label: "after" | "before";
  className?: string;
  handleDateChange: (val: Date | undefined, label: "after" | "before") => void;
}) => {
  const { t, i18n } = useTranslation("dashboard", {
    keyPrefix: "documents",
  });

  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);

  const currentLocale = locales[i18n.language] || enUS;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlDate = parseDate(params.get(label));
    if (urlDate) setDate(urlDate);
  }, [label]);

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            id="date"
            className="justify-between font-normal"
          >
            {date ? (
              date.toLocaleDateString(i18n.language)
            ) : (
              <span className="capitalize">
                {label === "after" ? t("après") : t("avant")}
              </span>
            )}
            <ChevronDownIcon />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            defaultMonth={date || new Date()} // ✅ centrer sur la date URL si dispo
            captionLayout="dropdown"
            locale={currentLocale}
            onSelect={(selected) => {
              setDate(selected);
              setOpen(false);
              handleDateChange(selected, label);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DateFilter;
